import { Box } from '@mui/material'
import React from 'react'
import LanguageCurrencyDropdownLogic from '../../../LanguageCurrencyDropdown'
import { HeaderLogo } from '../../shared/HeaderLogo'
import { HeaderNavigation } from '../../shared/HeaderNavigation'
import { DealsLink } from '../../shared/DealsLink'
import { SupportLink } from '../../shared/SupportLink'
import { UserDisplay } from '../../shared/UserDisplay'
import { SignInButton } from '../../shared/SignInButton'
import { UserMenu } from '../../shared/UserMenu'

interface HeaderProps {
    navLinks: { label: string; href: string; labelKey?: string }[]
    options: {
        language: { code: string; label: string; Flag: React.ComponentType<any> }[]
        currency: { code: string; label: string; Flag: React.ComponentType<any>; symbol: string }[]
    }
    selected: {
        language: { code: string; label: string; Flag: React.ComponentType<any> }
        currency: { code: string; label: string; Flag: React.ComponentType<any>; symbol: string }
    }
    menuAnchors: {
        user: null | HTMLElement
        language: null | HTMLElement
        currency: null | HTMLElement
    }
    onMenuOpen: (menu: 'user' | 'language' | 'currency', event: React.MouseEvent<HTMLElement>) => void
    onMenuClose: (menu: 'user' | 'language' | 'currency') => void
    onSelect: (type: 'language' | 'currency', value: any) => void
    modals: { signIn: boolean }
    onModalOpen: (modal: 'signIn') => void
    onModalClose: (modal: 'signIn') => void
    t: (key: string) => string
    tCurrency: (key: string) => string
    user: { signedIn: boolean; email?: string; displayName?: string; mobile?: string; countryCode?: string; firstName?: string }
    userDisplayName: string
    userMenuItems: { label: string; href: string }[]
    onSignOut: () => Promise<void>
    supportHref: string
    locale: string
    showLangCurrDropdown: boolean
    showDeals?: boolean
    isOffersPage?: boolean
    hideSignIn?: boolean
}

const FlyinHeader: React.FC<HeaderProps> = ({
    navLinks,
    options,
    selected,
    menuAnchors,
    onMenuOpen,
    onMenuClose,
    onSelect,
    modals,
    onModalOpen,
    onModalClose,
    t,
    tCurrency,
    user,
    userDisplayName,
    userMenuItems,
    onSignOut,
    supportHref,
    locale,
    showLangCurrDropdown,
    showDeals = true,
    isOffersPage = false,
    hideSignIn = false
}) => {
    return (
        <>
            <Box
                sx={{
                    width: '100%',
                    bgcolor: 'background.paper',
                    borderBottom: '1px solid',
                    borderColor: 'grey.200',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pl: { xs: 2, sm: 2.5 },
                    py: { xs: 1.5, sm: 2 },
                    pr: { xs: 2, sm: 2.5, md: 6 },
                    height: { xs: 48, sm: 54 }
                }}
            >
                {/* Left: Logo + Nav */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 2, sm: 3 } }}>
                    <HeaderLogo variant="flyin" locale={locale} />
                    <HeaderNavigation navLinks={navLinks} variant="flyin" t={t} />
                </Box>
                {/* Right: Actions */}
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: 1.5, sm: 2.5 },
                        fontSize: { xs: 13, sm: 14 },
                        fontWeight: 500,
                        color: '#1a2a3a'
                    }}
                >
                    <DealsLink locale={locale} t={t} hidden={!showDeals} variant="flyin" />
                    {/* Language/Currency Dropdown Button - Hidden on mobile */}
                    {showLangCurrDropdown && (
                        <Box sx={{ display: { xs: 'none', sm: 'none', md: 'block' } }}>
                            <LanguageCurrencyDropdownLogic
                                variant="flyin"
                                selectedLanguage={selected.language}
                                selectedCurrency={selected.currency}
                                languageOptions={options.language}
                                currencyOptions={options.currency}
                                onLanguageSelect={lang => onSelect('language', lang)}
                                onCurrencySelect={curr => onSelect('currency', curr)}
                                t={t}
                                tCurrency={tCurrency}
                                isOffersPage={isOffersPage}
                            />
                        </Box>
                    )}
                    <SupportLink href={supportHref} t={t} variant="flyin" />
                    {user.signedIn ? (
                        <UserDisplay
                            userDisplayName={userDisplayName}
                            onClick={e => onMenuOpen('user', e)}
                            variant="flyin"
                        />
                    ) : (
                        <SignInButton onClick={() => onModalOpen('signIn')} t={t} variant="flyin" hidden={hideSignIn} />
                    )}
                    <UserMenu
                        open={!!menuAnchors.user && user.signedIn}
                        anchorEl={menuAnchors.user}
                        menuItems={userMenuItems}
                        onClose={() => onMenuClose('user')}
                        onSignOut={onSignOut}
                        t={t}
                        variant="flyin"
                    />
                </Box>
            </Box>
        </>
    )
}

export { FlyinHeader }
export type { HeaderProps as FlyinHeaderProps }
